// ============================================================================
// Lecture d'une journée de planning — source unique
// ============================================================================
// Ces trois fonctions disaient la même chose à deux endroits : l'écran Planning
// et la vue d'ensemble multi-site. Elles vivent ici pour que les deux écrans ne
// puissent plus diverger — le boss compare les deux à l'œil, ils doivent lire
// exactement pareil.
// ============================================================================
import type { Schedule } from '@/types';

export type Slot = 'morning' | 'afternoon';

/** Heures travaillées dans la journée. Une absence ne compte pas d'heures. */
export function computeDailyHours(s: Schedule | undefined): number {
  if (!s || s.status !== 'WORK') return 0;
  const part = (start?: string | null, end?: string | null) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return Math.max(0, eh + em / 60 - (sh + sm / 60));
  };
  return part(s.morningStart, s.morningEnd) + part(s.afternoonStart, s.afternoonEnd);
}

/** Le créneau d'une demi-journée, tel qu'il s'affiche : « 09:00-12:30 ». */
export function slotLabel(s: Schedule | undefined, slot: Slot): string {
  if (!s || s.status !== 'WORK') return '—';
  if (slot === 'morning') return s.morningStart ? `${s.morningStart}-${s.morningEnd}` : '—';
  return s.afternoonStart ? `${s.afternoonStart}-${s.afternoonEnd}` : '—';
}

/** Le salarié travaille-t-il ce jour-là ? (au moins une demi-journée posée) */
export const isWorkingDay = (s: Schedule | undefined): boolean =>
  s?.status === 'WORK' && !!(s.morningStart || s.afternoonStart);

/** Une ligne de la fiche « horaires d'ouverture », telle que l'API la renvoie. */
export interface OpeningDay {
  dayOfWeek: number; // 0 = dimanche, 1 = lundi, … 6 = samedi
  closed?: boolean;
  morningOpen?: string | null;
  morningClose?: string | null;
  afternoonOpen?: string | null;
  afternoonClose?: string | null;
}

/**
 * L'officine ouvre-t-elle le dimanche ? (au moins une demi-journée)
 *
 * Toute la semaine affichée en dépend : sans configuration, le dimanche reste
 * fermé — c'est la convention du moteur côté serveur, on ne la contredit pas.
 */
export function opensOnSunday(openingHours: OpeningDay[] | undefined): boolean {
  const sunday = (openingHours ?? []).find((o) => o.dayOfWeek === 0);
  if (!sunday || sunday.closed) return false;
  return sunday.morningOpen != null || sunday.afternoonOpen != null;
}

/**
 * Nombre de jours à afficher dans une semaine : 6 (lundi→samedi) ou 7 quand
 * l'officine ouvre le dimanche. Les écrans qui dessinent une semaine passent
 * tous par ici pour ne pas diverger.
 */
export function weekDayCount(openingHours: OpeningDay[] | undefined): number {
  return opensOnSunday(openingHours) ? 7 : 6;
}

/**
 * Dernier recours quand l'officine n'a aucun horaire renseigné pour un jour.
 * Le pré-remplissage normal vient de la fiche « horaires d'ouverture » (voir
 * `slotDefaults`) : ces valeurs-ci ne doivent jamais primer sur elle.
 */
export const SLOT_FALLBACK = {
  morningStart: '09:00',
  morningEnd: '12:30',
  afternoonStart: '14:00',
  afternoonEnd: '19:30',
};

export type SlotDefaults = typeof SLOT_FALLBACK;

/**
 * Horaires à pré-remplir pour un jour donné : ceux de l'officine, sinon le
 * repli. Source unique — la semaine type du salarié et la saisie des heures
 * réellement travaillées proposent ainsi exactement les mêmes valeurs.
 *
 * Une demi-journée fermée n'a pas d'horaire à proposer : on retombe alors sur
 * le repli plutôt que sur une case vide, parce qu'on peut vouloir y poser du
 * travail hors ouverture (ménage, préparation de commandes — cf. M9-1).
 */
export function slotDefaults(hours: OpeningDay[] | undefined, dayOfWeek: number): SlotDefaults {
  const row = (hours ?? []).find((o) => o.dayOfWeek === dayOfWeek);
  const open = row && !row.closed ? row : undefined;
  return {
    morningStart: open?.morningOpen ?? SLOT_FALLBACK.morningStart,
    morningEnd: open?.morningClose ?? SLOT_FALLBACK.morningEnd,
    afternoonStart: open?.afternoonOpen ?? SLOT_FALLBACK.afternoonStart,
    afternoonEnd: open?.afternoonClose ?? SLOT_FALLBACK.afternoonEnd,
  };
}

/** Libellés des jours, dans l'ordre d'affichage (lundi en tête). */
export const WEEK_DAY_LABELS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/** Numéros ISO des jours (`Date.getDay()`), dans le même ordre d'affichage. */
export const WEEK_DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 0];

/**
 * Demi-journées fermées de la pharmacie, par jour de semaine, telles que les
 * lisent le planning et la vue d'ensemble.
 */
export function closedByWeekday(
  openingHours: OpeningDay[] | undefined
): Map<number, { morning: boolean; afternoon: boolean }> {
  const map = new Map<number, { morning: boolean; afternoon: boolean }>();
  (openingHours ?? []).forEach((o) => {
    map.set(o.dayOfWeek, {
      morning: !!o.closed || o.morningOpen == null,
      afternoon: !!o.closed || o.afternoonOpen == null,
    });
  });
  return map;
}
